import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounceValue } from 'usehooks-ts';
import NumberInput from '~/components/Inputs/NumberInput';
import { DoseId } from '~/domain/changes/ids';
import { DoseDisplayView } from '~/hooks/dose/doseDisplayView';
import useDose from '~/hooks/patient/dose/useDose';

const DOSE_MIN_VALUE = 1;
const DOSE_MAX_VALUE = 1000;
const DOSE_STEP = 1;

/** A typed rescaling percentage together with the dose it was typed for. */
type PendingScale = {
    doseId: DoseId | undefined;
    percent: number | null;
};

const DoseRescaler = ({ dose }: { dose?: DoseDisplayView }) => {
    const doseId = dose?.id;
    const doseScale = dose?.doseScale ?? 1.0;
    const { setDoseScale } = useDose();
    const [pending, setPending] = useState<PendingScale>({
        doseId,
        percent: doseScale * 100,
    });
    const intl = useIntl();
    const [error, setError] = useState<string | null>(null);
    const [debouncedPending] = useDebounceValue(pending, 100);

    // The selected dose can change while this input is mounted and visible:
    // a completed prediction switches the selected dose with no click at
    // all. Re-seed from the new dose rather than leaving the previous dose's
    // percentage on screen. React's documented "adjust state during render"
    // pattern — no extra frame is committed with the stale value.
    if (doseId !== pending.doseId) {
        setPending({ doseId, percent: doseScale * 100 });
        setError(null);
    }

    const handleInputChange = (newValue: number | null) => {
        setPending({ doseId, percent: newValue });
    };

    useEffect(() => {
        const { doseId: pendingDoseId, percent } = debouncedPending;
        // Drop a value still in the debounce buffer from a dose that is no
        // longer shown. Without this the previous dose's percentage lands in
        // the NEW dose's display store, silently rescaling its max-dose
        // readout and its DVH — a change the user never asked for and has no
        // way to attribute.
        if (pendingDoseId !== doseId || !doseId) {
            return;
        }
        if (percent === null || isNaN(percent)) {
            return;
        }
        const validValue = validateValue(percent);
        if (validValue !== percent) {
            setError(
                intl.formatMessage(
                    { id: 'valueMustBeBetween' },
                    { min: DOSE_MIN_VALUE, max: DOSE_MAX_VALUE }
                )
            );
            return;
        }
        setError(null);
        // Nothing to commit when the field still holds what the store already
        // has — the seed value on mount, or a re-run triggered purely by
        // `setDoseScale` changing identity on a dose switch.
        if (percent === doseScale * 100) {
            return;
        }
        setDoseScale(percent / 100);
    }, [debouncedPending, doseId, doseScale, setDoseScale, intl]);

    const validateValue = (value: number) => {
        return Math.min(Math.max(value, DOSE_MIN_VALUE), DOSE_MAX_VALUE);
    };

    return (
        <Box>
            <NumberInput
                value={pending.percent}
                precision={1}
                fullWidth
                label={<FormattedMessage id="doseScaling" />}
                error={error}
                min={DOSE_MIN_VALUE}
                max={DOSE_MAX_VALUE}
                step={DOSE_STEP}
                onChange={handleInputChange}
                disabled={!dose?.generated}
                tooltipPlacement="bottom-start"
                data-testid="rescale-dose-button"
            />
            <Typography variant="body2" fontSize={11} mt={0.5}>
                <FormattedMessage id="doseScalingInfo" />
            </Typography>
        </Box>
    );
};

export default DoseRescaler;
